import { Alert, AlertDescription } from "@/ds/shadcn/components/ui/alert";
import { useConcludeForgotPassword } from "@/features/auth/api/useConcludeForgotPassword";
import { AuthHeader } from "@/features/auth/components/AuthHeader";
import { AuthHeaderSubtitle } from "@/features/auth/components/AuthHeaderSubtitle";
import { AuthHeaderTitle } from "@/features/auth/components/AuthHeaderTitle";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useFocusOnLoad } from "@/lib/common/useFocus";
import { useQueryParams } from "@/lib/common/useQueryParams";
import { useTitle } from "@/lib/common/useTitle";
import { Form } from "@/lib/form/components/Form";
import { useAppForm } from "@/lib/form/useAppForm";
import { CheckCircle2Icon, TimerResetIcon } from "lucide-react";
import { useEffect, useMemo, type FC } from "react";
import { useNavigate } from "react-router";
import { z } from "zod";

export const PageConcludeForgotPassword: FC = () => {
	useTitle("Forgot Password");

	const ref = useFocusOnLoad();
	const concludeForgotPassword = useConcludeForgotPassword();
	const navigate = useNavigate();
	const queryParams = useQueryParams();
	const code = useMemo(() => queryParams.get("code"), [queryParams]);
	const userId = useMemo(() => queryParams.get("userId"), [queryParams]);
	const codeExpired = useMemo(() => concludeForgotPassword.error && concludeForgotPassword.error.problem.problemType === "CodeExpired", [concludeForgotPassword.error]);

	const validators = useMemo(() => z.object({
		password: z.string().min(8, "Your password must be at least 8 characters long"),
		confirmPassword: z.string()
	}).refine(x => x.password === x.confirmPassword, {
		message: "Passwords must match",
		path: ["confirmPassword"]
	}), []);

	const form = useAppForm({
		defaultValues: {
			password: "",
			confirmPassword: ""
		},
		validators: {
			onMount: validators,
			onChange: validators
		},
		onSubmit: async ({ value }) => {
			if (!userId || !code) {
				return;
			}

			await concludeForgotPassword.mutateAsync({
				userId,
				forgotPasswordCode: code,
				newPassword: value.password
			});
		}
	});

	useEffect(() => {
		if (!concludeForgotPassword.isSuccess) {
			return;
		}

		const timeout = setTimeout(() => navigate("../.."), 2000);
		return () => clearTimeout(timeout);
	}, [concludeForgotPassword.isSuccess]);

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit();
	};

	return (
		<>
			<AuthHeader>
				<AuthHeaderTitle>Forgot Password</AuthHeaderTitle>
				<AuthHeaderSubtitle>Please provide a new password to use</AuthHeaderSubtitle>
			</AuthHeader>

			{
				!concludeForgotPassword.isSuccess && !codeExpired &&
				<Form onSubmit={onSubmit}>
					<form.AppField name="password">
						{
							field => <field.TextField label="New Password" type="password" ref={ref} required />
						}
					</form.AppField>

					<form.AppField name="confirmPassword">
						{
							field => <field.TextField label="Confirm New Password" type="password" required />
						}
					</form.AppField>
					<ErrorDisplay error={concludeForgotPassword.error} />
					<form.AppForm>
						<form.SubmitButton label="Confirm" />
					</form.AppForm>
				</Form>
			}

			{
				codeExpired &&
				<Alert variant="destructive">
					<TimerResetIcon />
					<AlertDescription>
						Your password reset link has expired, please try again.
					</AlertDescription>
				</Alert>
			}

			{
				concludeForgotPassword.isSuccess &&
				<Alert>
					<CheckCircle2Icon />
					<AlertDescription>
						Your password has been successfully reset.
					</AlertDescription>
				</Alert>
			}
		</>
	);
};
